import React, { useEffect } from 'react';
import { setLocation, setLocationName } from '../../Redux/Reducer/Reducer';
import { useDispatch, useSelector } from 'react-redux';
import { SetSessionStorageData, GetSessionStorageData } from '../../Utilities/Helpers/Helper';
import { AUTHENTICATION } from '../../Constants/Constants';

interface LocationDetails {
  city?: string;
  country?: string;
}

interface Location {
  lat: number;
  lng: number;
}

const LatLanComponent = ({ region, country }: { region?: string; country?: string }) => {
  const dispatch = useDispatch();
  const isGoogleMapsLoaded = useSelector((state: any) => state.googleMapLoadedReducer?.isGoogleMapLoaded);

  const getLocationDetails = async (lat: number, lng: number): Promise<LocationDetails | null> => {
    if (typeof window === 'undefined' || !window.google || !window.google.maps) return null;

    const geocoder = new window.google.maps.Geocoder();
    return new Promise((resolve) => {
      geocoder.geocode({ location: { lat, lng } }, (
        results: google.maps.GeocoderResult[] | null,
        status: google.maps.GeocoderStatus
      ) => {
        if (status === "OK" && results?.[0]) {
          const addressComponents = results[0].address_components;
          const city = addressComponents.find((c: any) =>
            c.types.includes('locality') || c.types.includes('administrative_area_level_1')
          );
          const countryComp = addressComponents.find((c: any) => c.types.includes('country'));
          resolve({
            city: city?.long_name,
            country: countryComp?.long_name
          });
        } else {
          resolve(null);
        }
      });
    });
  };

  useEffect(() => {
    if (!country || !isGoogleMapsLoaded) return;

    const existingLocation = GetSessionStorageData(AUTHENTICATION.SHARED.CURRENT_LOCATION);
    if (existingLocation) {
      console.log('LatLanComponent: Location already in session, skipping geocoding');
      return;
    }

    const source = GetSessionStorageData('LOCATION_SOURCE');
    if (source === 'user_selection' || source === 'fallback') {
      console.log('LatLanComponent: Skipped due to user/fallback source');
      return;
    }

    const addressToGeocode = region && country ? `${region}, ${country}` : country;

    const geocodeAddress = async () => {
      if (typeof window === 'undefined' || !window.google || !window.google.maps) {
        console.warn('Google Maps not ready');
        return;
      }

      try {
        const geocoder = new window.google.maps.Geocoder();
        const results = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
          geocoder.geocode(
            { address: addressToGeocode },
            (res: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
              if (status === 'OK' && res?.[0]) {
                resolve(res);
              } else {
                reject(new Error(`Geocode failed: ${status}`));
              }
            }
          );
        });

        const loc = results[0].geometry.location;
        const location: Location = { lat: loc.lat(), lng: loc.lng() };
        const details = await getLocationDetails(location.lat, location.lng);

        const displayName = details?.city
          ? `${details.city}, ${details.country}`
          : details?.country || country;

        dispatch(setLocation(location));
        dispatch(setLocationName(displayName));

        SetSessionStorageData(AUTHENTICATION.SHARED.CURRENT_LOCATION, JSON.stringify(location));
        SetSessionStorageData(AUTHENTICATION.SHARED.COUNTRY_NAME, details?.country || country);
        SetSessionStorageData(AUTHENTICATION.SHARED.REGION_NAME, details?.city || region);

        const locationJSON = JSON.stringify(location);
        SetSessionStorageData(AUTHENTICATION.SHARED.SCHOOL_LOCATION, locationJSON);
        SetSessionStorageData(AUTHENTICATION.SHARED.UNIVERSITY_LOCATION, locationJSON);
        SetSessionStorageData(AUTHENTICATION.SHARED.TUTOR_LOCATION, locationJSON);
        SetSessionStorageData(AUTHENTICATION.SHARED.ACTIVITIES_LOCATION, locationJSON);

        console.log('LatLanComponent: Location updated via geocoding', { location, displayName });
      } catch (error) {
        console.error('LatLanComponent: Geocoding error:', error);
      }
    };

    geocodeAddress();
  }, [country, region, isGoogleMapsLoaded, dispatch]);

  return null;
};

export default LatLanComponent;